import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@utils/icons';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { MARKUP_COLORS, resolveMarkupColor, type MarkupColor, type MarkupColorId } from '../../../utils/markupTypes';
import styles from './Toolbar.module.css';

/** Swatches too light to carry a white check — theirs is drawn dark. */
const LIGHT_SWATCHES = new Set<MarkupColorId>(['amber', 'white']);

interface IColorSwatchesProps {
  color: MarkupColor;
  disabled: boolean;
  onColorChange: (color: MarkupColor) => void;
}

/** The palette swatches plus the any-color picker. The chosen one carries a
 *  check — the swatch is the color, so nothing may be drawn around it that
 *  changes how that color reads. The picker is the whole spectrum until a color
 *  is picked from it, and keeps its rainbow ring around that color afterwards. */
export function ColorSwatches({ color, disabled, onColorChange }: IColorSwatchesProps) {
  const { t } = useTranslation();
  const isCustomColor = color.startsWith('#');
  // The picker is UNCONTROLLED on purpose. A controlled `value` is written back
  // into the OS color panel while it is open, which re-syncs the panel to the
  // old value — every pick then comes back as that value instead of the chosen
  // one. What is on screen is the dot below, drawn from our own state.
  const picker = useRef<HTMLInputElement>(null);

  return (
    <div className={styles.toolGroup}>
      {MARKUP_COLORS.map((id) => (
        <Tooltip key={id} content={t(`documents.markup.colors.${id}`)}>
          <button
            type="button"
            className={`${styles.swatch} ${LIGHT_SWATCHES.has(id) ? styles.swatchLight : ''}`}
            style={{ background: `var(--markup-${id})` }}
            onClick={() => onColorChange(id)}
            disabled={disabled}
            aria-pressed={color === id}
            aria-label={t(`documents.markup.colors.${id}`)}
          >
            {color === id && <CheckIcon size={12} />}
          </button>
        </Tooltip>
      ))}

      {/* The spectrum, with the picked color as a dot inside it. */}
      <span className={styles.customSwatchRing}>
        {isCustomColor && <span className={styles.customSwatchDot} style={{ background: color }} aria-hidden="true" />}
        <Tooltip content={t('documents.markup.customColor')}>
          <input
            ref={picker}
            type="color"
            className={styles.customSwatch}
            defaultValue={resolveMarkupColor(color) || '#888888'}
            onChange={(e) => onColorChange(e.target.value)}
            // Clicking the picker selects the color it holds, the way clicking
            // a swatch does — otherwise re-picking the same custom color after
            // a detour through the palette fires no change at all.
            onClick={() => { const held = picker.current?.value; if (held && held !== color) onColorChange(held); }}
            disabled={disabled}
            aria-label={t('documents.markup.customColor')}
          />
        </Tooltip>
        {isCustomColor && <span className={styles.customSwatchCheck} aria-hidden="true"><CheckIcon size={12} /></span>}
      </span>
    </div>
  );
}
